import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { toJS } from 'mobx'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import { useCartDrawer } from '../components/CartDrawerProvider'
import type {
  CartItem,
  PhotoBookCartItemWarning,
  TileCartItemWarning,
} from 'stores/cartStore/CartItem'
import { getEditRoute } from '../cart.utils'
import {
  getPhotoBookWarningAnalytics,
  getPhotoBookWarningDisplay,
  getTileWarningAnalytics,
  getTileWarningDisplay,
  isBlockingCartWarning,
  type WarningDisplay,
} from '../cartWarning.config'
import { useBagAnalytics } from './useBagAnalytics'

export type CartItemWarningView = {
  text: string
  ctaText: string
  key: string
  onFixClick: () => void
  onDismiss?: () => void
}

type ActiveWarning =
  | { kind: 'tile'; warning: TileCartItemWarning }
  | { kind: 'photo-book'; warning: PhotoBookCartItemWarning }

function selectActiveWarning<
  Warning extends TileCartItemWarning | PhotoBookCartItemWarning,
>(warnings: Warning[] | undefined, dismissed: string[]): Warning | null {
  return (
    warnings?.find(isBlockingCartWarning) ??
    warnings?.find((warning) => !dismissed.includes(warning.type)) ??
    null
  )
}

function getActiveWarning(
  item: CartItem,
  dismissed: string[]
): ActiveWarning | null {
  switch (item.productType) {
    case PRODUCT_TYPES.CLASSIC: {
      const warning = selectActiveWarning(item.warnings, dismissed)
      return warning ? { kind: 'tile', warning } : null
    }
    case PRODUCT_TYPES.PHOTO_BOOK: {
      const warning = selectActiveWarning(item.warnings, dismissed)
      return warning ? { kind: 'photo-book', warning } : null
    }
    default:
      return null
  }
}

function getWarningDisplay(active: ActiveWarning): WarningDisplay {
  switch (active.kind) {
    case 'tile':
      return getTileWarningDisplay(active.warning)
    case 'photo-book':
      return getPhotoBookWarningDisplay(active.warning)
  }
}

function getWarningAnalytics(active: ActiveWarning) {
  switch (active.kind) {
    case 'tile':
      return getTileWarningAnalytics(active.warning)
    case 'photo-book':
      return getPhotoBookWarningAnalytics(active.warning)
  }
}

export function useCartItemWarning(item: CartItem): CartItemWarningView | null {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { closeCart } = useCartDrawer()
  const cartStore = useCartStore()
  const { trackBagItemEvent, trackBagItemEventOnce } = useBagAnalytics()

  const dismissed = item.dismissedWarningTypes ?? []
  const active = getActiveWarning(item, dismissed)

  useEffect(() => {
    if (active) {
      const { displayed, props } = getWarningAnalytics(active)
      trackBagItemEventOnce(
        displayed,
        `${item.id}:${active.warning.type}`,
        item,
        props
      )
    }
  }, [active, item, trackBagItemEventOnce])

  if (!active) return null

  const display = getWarningDisplay(active)
  const route = getEditRoute(item)
  const warningAnalytics = getWarningAnalytics(active)

  return {
    text: t(display.labelKey, display.labelVars),
    ctaText: t(display.ctaKey),
    key: active.warning.type,
    onFixClick: () => {
      trackBagItemEvent(warningAnalytics.tapped, item, warningAnalytics.props)
      if (!route) return
      closeCart()
      navigate(route, { state: { warningResolve: toJS(active.warning) } })
    },
    onDismiss: isBlockingCartWarning(active.warning)
      ? undefined
      : () => {
          trackBagItemEvent(
            warningAnalytics.dismissed,
            item,
            warningAnalytics.props
          )
          cartStore.dismissWarning(item.id, active.warning.type)
        },
  }
}
